import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../lib/api';
import type { GridSize, UiPrefs } from '../types';

// MF-111 — per-user visual prefs. MF-245 removed the theme presets + accent color
// (the switch flickered/jumped); only grid-view card density (S/M/L) remains.
// localStorage gives an instant first paint; the server is the cross-device source
// of truth and reconciles once the user is known. Writes are optimistic +
// debounced to /api/me/ui-prefs.

const LS_KEY = 'markflow-ui-prefs';

const DEFAULTS: Pick<UiPrefs, 'gridSize'> = {
  gridSize: 'M',
};

function readLocal(): UiPrefs {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as UiPrefs) : {};
  } catch {
    return {};
  }
}

function writeLocal(prefs: UiPrefs): void {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable (private mode quota, disabled) — no-op.
  }
}

interface UiPrefsContextValue {
  gridSize: GridSize;
  setGridSize: (size: GridSize) => void;
}

const UiPrefsContext = createContext<UiPrefsContextValue | null>(null);

export function UiPrefsProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  const [prefs, setPrefs] = useState<UiPrefs>(() => ({ ...DEFAULTS, ...readLocal() }));
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const gridSize = (prefs.gridSize ?? 'M') as GridSize;

  // Reconcile with the server once the user is known (cross-device sync).
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api
      .getUiPrefs()
      .then((server) => {
        if (cancelled) return;
        setPrefs((prev) => {
          const merged = { ...prev, ...server };
          writeLocal(merged);
          return merged;
        });
      })
      .catch(() => {
        // Best-effort — keep the localStorage view on failure.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const update = useCallback(
    (patch: UiPrefs) => {
      setPrefs((prev) => {
        const next = { ...prev, ...patch };
        writeLocal(next);
        return next;
      });
      if (!enabled) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        api.setUiPrefs(patch).catch(() => {
          // Best-effort persist; the localStorage value already updated.
        });
      }, 400);
    },
    [enabled],
  );

  const value: UiPrefsContextValue = {
    gridSize,
    setGridSize: (size) => update({ gridSize: size }),
  };

  return <UiPrefsContext.Provider value={value}>{children}</UiPrefsContext.Provider>;
}

export function useUiPrefs(): UiPrefsContextValue {
  const ctx = useContext(UiPrefsContext);
  if (!ctx) throw new Error('useUiPrefs must be used within UiPrefsProvider');
  return ctx;
}
